import { classNames } from "primereact/utils";
import { FloatingPanel } from "../../common/components/common/FloatingPanel/FloatingPanel";
import { FiltersSidePanel } from "../../common/components/filters/FiltersSidePanel";
import { RenderWhen } from "../../common/infrastructure/components/RenderWhen";
import { AiAssistantHeader } from "./components/AiAssistantHeader";
import { AiBanner } from "./components/AiBanner";
import { AiComposer } from "./components/AiComposer";
import { AiConversationList } from "./components/AiConversationList";
import { AiImagePreviewDialog } from "./components/AiImagePreviewDialog";
import { AiMessageList } from "./components/AiMessageList";
import { useAiAssistantPage } from "./hooks/useAiAssistantPage";
import { aiAssistantTexts } from "./models/aiAssistantModels";
import styles from "./AiAssistantPage.module.scss";

/** "Поддръжка" for staff: the AI assistant, with the human team's tickets one button away. */
export const AiAssistantPage = () => {
  const page = useAiAssistantPage();
  const { composer } = page;

  const conversationList = (
    <AiConversationList
      conversations={page.conversations}
      selectedId={page.selectedConversationId}
      isLoading={page.isConversationsLoading}
      isDeleting={page.isDeleting}
      onSelect={page.selectConversation}
      onDelete={page.requestDeleteConversation}
    />
  );

  return (
    <>
      <FloatingPanel variant="glass" className={styles.page} maxHeight={page.keyboardMaxHeight}>
        <div ref={page.panelTopRef} className={styles.page_anchor} />

        <AiAssistantHeader
          isDesktopOrTablet={page.isDesktopOrTablet}
          usedPercent={page.usedPercent}
          usedPercentLabel={page.usedPercentLabel}
          resetDateLabel={page.resetDateLabel}
          hasUnreadTeamReplies={page.hasUnreadTeamReplies}
          onOpenConversations={page.openDrawer}
          onNewConversation={page.startNewConversation}
          onOpenTeamRequests={page.openTeamRequests}
        />

        <div className={styles.body}>
          <RenderWhen condition={page.isDesktopOrTablet}>
            <aside className={styles.sidebar} aria-label={aiAssistantTexts.conversations}>
              {conversationList}
            </aside>
          </RenderWhen>

          <section
            className={classNames(styles.chat, { [styles.chat_dragging]: composer.isDragging })}
            aria-label={aiAssistantTexts.title}
            {...composer.dropZoneProps}
          >
            <AiBanner
              banner={page.banner}
              resetDateLabel={page.resetDateLabel}
              onNewConversation={page.startNewConversation}
              onWriteToTeam={page.writeToTeam}
            />

            <AiMessageList
              containerRef={page.messagesContainerRef}
              entries={page.entries}
              isLoading={page.isConversationLoading}
              isSending={page.isSending}
              showEmptyState={page.showEmptyState}
              onPickExample={composer.fill}
              onRetry={page.retryLast}
              onOpenImage={page.openImagePreview}
            />

            <AiComposer
              composer={composer}
              isSending={page.isSending}
              hideFooter={page.isPhoneKeyboardOpen}
              onStop={page.stop}
            />

            <RenderWhen condition={composer.isDragging}>
              <div className={styles.drop_overlay} aria-hidden="true">
                {aiAssistantTexts.dropHere}
              </div>
            </RenderWhen>
          </section>
        </div>
      </FloatingPanel>

      <RenderWhen condition={!page.isDesktopOrTablet}>
        <FiltersSidePanel
          visible={page.isDrawerOpen}
          onHide={page.closeDrawer}
          title={aiAssistantTexts.conversations}
          className={styles.drawer}
          position="left"
        >
          {conversationList}
        </FiltersSidePanel>
      </RenderWhen>

      <AiImagePreviewDialog url={page.previewImageUrl} onHide={page.closeImagePreview} />
    </>
  );
};
